import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ViewMode } from "../../libs/gantt-task-react";
import { getStartEndDateForProject, getTotalProjectProgress } from "../helper";
import "../../libs/gantt-task-react/dist/index.css";
import "./TaskManagementPage.css";
import { useGetTasksQuery, useBulkUpdateTasksMutation } from '../../services/tasksApi';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import TopMenu from "../TopMenu";
import GanttChart from "../GanttChart";
import { useGetUserQuery, useGetUsersQuery, useUpdateUserSettingsMutation } from "../../services/usersApi";
import { useGetInstitutionsQuery } from "../../services/institutionsApi";
import { useChannelQuery } from "../../services/websocketApi.ts";
import TaskContextMenu from "../toolbar/TaskContextMenu";
import TaskModal from "../toolbar/TaskModal";
import { useCreateTaskMutation, useDeleteTaskMutation } from '../../services/tasksApi';
import ProjectModal from '../toolbar/ProjectModal';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useToast } from "../../hooks/useToast.ts";

const TaskManagementPage = () => {
    const [view, setView] = useState(ViewMode.Week);
    const [viewDate, setViewDate] = useState(new Date());
    const [projectFilter, setProjectFilter] = useState({ name: 'Wszystkie', _id: 'all' });
    const toast = useToast();
    const handleProjectChange = (project) => {
        setProjectFilter(project);
    };

    const [tasks, setTasks] = useState([]);
    const [selectedUser, setSelectedUser] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState([]);
    const [selectedProjectStatus, setSelectedProjectStatus] = useState('active');
    const [showProjects, setShowProjects] = useState(true);
    
    const [createTask] = useCreateTaskMutation();
    const [deleteTask] = useDeleteTaskMutation();
    const [bulkUpdateTasks] = useBulkUpdateTasksMutation();
    // Inside the TaskManagementPage component, add these state variables:
    const [modalVisible, setModalVisible] = useState(false);
    const [modalTask, setModalTask] = useState(null);
    // Inside the TaskManagementPage component, add these state variables:
    const [projectModalVisible, setProjectModalVisible] = useState(false);
    const [projectModalProject, setProjectModalProject] = useState(null);
    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);
    const [editorMode, setEditorMode] = useState(true)
    const [visible, setVisible] = useState(false)
    const [rowHeight, setRowHeight] = useState(50);
    const [columnWidth, setColumnWidth] = useState(65);
    const [barFill, setBarFill] = useState(80);
    const [sortBy, setSortBy] = useState('projectName');
    const [columnVisibility, setColumnVisibility] = useState({
        coordinator: true,
        executor: true,
        branch: true,
        name: true,
        start: true,
        end: true,
        progress: true
    });
    const [columnOrder, setColumnOrder] = useState(['coordinator', 'executor', 'branch', 'name', 'start', 'end', 'progress']);
    const [columnWidths, setColumnWidths] = useState({
        coordinator: 105,
        executor: 105,
        branch: 105,
        name: 200,
        start: 105,
        end: 105,
        progress: 105
    });
    const [collapsedChains, setCollapsedChains] = useState(new Set());

    const {
        data,
        isSuccess: isTasksLoaded,
        isLoading: isTasksLoading,
        isFetching: isTasksFetching,
        refetch: refetchTasks,
    } = useGetTasksQuery(
        { status: selectedProjectStatus, view: 'gantt' },
        { refetchOnMountOrArgChange: true }
    );

    const isInitialGanttLoading = isTasksLoading;
    const isGanttRefreshing = !isTasksLoading && isTasksFetching;

    const [contextMenu, setContextMenu] = useState({
        visible: false,
        position: { x: 0, y: 0 },
        task: null,
    });

    const [mousePos, setMousePos] = useState({
        position: { x: 0, y: 0 },
    });

    const ganttContainerRef = useRef(null);
    const { data: taskEvents } = useChannelQuery('tasks');
    const lastProcessedTaskEventKeyRef = useRef(null);
    const lastRefetchAtRef = useRef(0);
    const refetchTasksRef = useRef(refetchTasks);
    const pendingTaskUpdatesRef = useRef(new Set());
    const { data: user, isUserLoading } = useGetUserQuery();
    const { data: users } = useGetUsersQuery();
    const { data: institutions } = useGetInstitutionsQuery();
    const [updateUserSettings] = useUpdateUserSettingsMutation();

    // Initialize settings from user profile
    useEffect(() => {
        if (user) {
            if (user.rowHeight !== undefined) setRowHeight(user.rowHeight);
            if (user.columnWidth !== undefined && !isNaN(user.columnWidth)) setColumnWidth(user.columnWidth);
            if (user.barFill !== undefined && !isNaN(user.barFill)) setBarFill(user.barFill);
            if (user.sortBy !== undefined) setSortBy(user.sortBy);
            if (user.columnVisibility !== undefined) setColumnVisibility(user.columnVisibility);
            if (user.columnOrder !== undefined) setColumnOrder(user.columnOrder);
            if (user.columnWidths !== undefined) {
                // Validate all columnWidths values
                const validatedWidths = {};
                Object.keys(user.columnWidths).forEach(key => {
                    const width = user.columnWidths[key];
                    validatedWidths[key] = !isNaN(width) && width > 0 ? width : columnWidths[key] || 105;
                });
                setColumnWidths(validatedWidths);
            }
        }
    }, [user]);

    useEffect(() => {
        refetchTasksRef.current = refetchTasks;
    }, [refetchTasks]);

    useEffect(() => {
        // Track recent successful task data updates to avoid redundant websocket refetch.
        if (data) {
            lastRefetchAtRef.current = Date.now();
        }
    }, [data]);

    useEffect(() => {
        const messages = taskEvents?.messages || [];
        const latest = messages[messages.length - 1];
        if (!latest) return;

        const latestKey = latest._id || `${latest.id || ''}-${latest.author || ''}-${latest.message || ''}`;
        if (!latestKey || lastProcessedTaskEventKeyRef.current === latestKey) return;
        lastProcessedTaskEventKeyRef.current = latestKey;

        // Mutations already invalidate Tasks and refetch; skip immediate duplicate refetches.
        const now = Date.now();
        if (now - lastRefetchAtRef.current < 1200) return;

        lastRefetchAtRef.current = now;
        refetchTasksRef.current();
    }, [taskEvents?.messages]);

    useEffect(() => {
        const handleMouseMove = (event) => {
            setMousePos({
                position: {
                    x: event.clientX + window.scrollX,
                    y: event.clientY + window.scrollY
                },
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Zoom in/out with Ctrl+Scroll to change view mode
    useEffect(() => {
        const handleWheel = (event) => {
            // Check if Ctrl key is pressed
            if (event.ctrlKey) {
                // Prevent both default browser zoom and Gantt's scroll behavior
                event.preventDefault();
                event.stopPropagation();
                
                // Define view mode order for zooming
                const viewModes = [
                    ViewMode.Day,
                    ViewMode.Week,
                    ViewMode.Month,
                    ViewMode.QuarterYear,
                    ViewMode.Year
                ];
                
                const currentIndex = viewModes.indexOf(view);
                
                // Scroll down (deltaY > 0) = zoom out (increase time range)
                // Scroll up (deltaY < 0) = zoom in (decrease time range)
                if (event.deltaY > 0) {
                    // Zoom out
                    if (currentIndex < viewModes.length - 1) {
                        setView(viewModes[currentIndex + 1]);
                    }
                } else if (event.deltaY < 0) {
                    // Zoom in
                    if (currentIndex > 0) {
                        setView(viewModes[currentIndex - 1]);
                    }
                }
            }
        };

        const ganttContainer = ganttContainerRef.current;
        if (ganttContainer) {
            // Use capture phase to intercept before Gantt's wheel handler
            ganttContainer.addEventListener('wheel', handleWheel, { passive: false, capture: true });
        }

        return () => {
            if (ganttContainer) {
                ganttContainer.removeEventListener('wheel', handleWheel, { capture: true });
            }
        };
    }, [view]);

    const handleDoubleClick = (task) => {
        // Use the current mousePos value for positioning the context menu.
        setContextMenu({
            visible: true,
            position: { ...mousePos.position },
            task: task,
        });
    };

    // Add this click outside handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contextMenu.visible && !e.target.closest('.p-menu')) {
                setContextMenu({ ...contextMenu, visible: false });
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [contextMenu]);

    const handleTaskChange = async (task) => {
        const taskUpdateKey = task._id || task.id;
        if (taskUpdateKey && pendingTaskUpdatesRef.current.has(taskUpdateKey)) {
            return tasks;
        }

        // Check if user has permission to modify this task
        if (user?.type !== "admin") {
            if (user?.type === "manager") {
                const project = tasks.find(t => t.id === (task.type === 'project' ? task.id : task.project));
                if (!project || !project.coordinators?.includes(user._id)) {
                    toast("Nie masz uprawnień do modyfikacji tego zadania", "error");
                    return tasks;
                }
            } else {
                toast("Nie masz uprawnień do modyfikacji zadań", "error");
                return tasks;
            }
        }

        if (task.dependencies) {
            if (task.dependencies.length > 0 && task.dependencies[0]) {
                const BaseTask = tasks.filter((baseTask) => baseTask.id === task.dependencies[0])

                if (BaseTask[0].start <= task.start) {
                    changeTask();
                }
                else {
                    setVisible(true)
                }

                return tasks
            }
            else {
                changeTask();
            }
        }
        async function changeTask() {
            try {
                if (taskUpdateKey) {
                    pendingTaskUpdatesRef.current.add(taskUpdateKey);
                }

                const taskToUpdate = {
                    ...task,
                    id: task.id,
                    type: task.type,
                    project: task.project,
                    start: task.start || new Date(),
                    end: task.end || new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
                    progress: task.progress || 0,
                    ...(task.description !== undefined && { description: task.description }),
                    ...(task.user && { user: task.user })
                };

                let startDate = taskToUpdate.start instanceof Date ? taskToUpdate.start : new Date(taskToUpdate.start);
                let endDate = taskToUpdate.end instanceof Date ? taskToUpdate.end : new Date(taskToUpdate.end);
                if (isNaN(startDate.getTime())) startDate = new Date();
                if (isNaN(endDate.getTime())) endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);

                const optimisticTask = { ...taskToUpdate, start: startDate, end: endDate };
                const taskWithDates = optimisticTask;

                const isProjectStatusChange = taskWithDates.type === "project" &&
                    taskWithDates.status !== undefined &&
                    tasks.find(t => t.id === taskWithDates.id)?.status !== taskWithDates.status;

                // Collect ALL cascading changes in one place, then send ONE bulk update
                const cascadingUpdates = [];

                setTasks(prevTasks => {
                    let newTasks = prevTasks.map(t => (t.id === task.id ? optimisticTask : t));

                    let dependentTasks = [];

                    if (taskWithDates.type === "project") {
                        dependentTasks = newTasks.filter(t => t.project === taskWithDates.id);

                        // If status changed, update all subtasks with the new status
                        if (isProjectStatusChange) {
                            dependentTasks = dependentTasks.map(dt => ({
                                ...dt,
                                status: taskWithDates.status
                            }));
                            newTasks = newTasks.map(t => {
                                const updated = dependentTasks.find(dt => dt.id === t.id);
                                return updated || t;
                            });
                            // Collect status-changed subtasks for bulk update
                            dependentTasks.forEach(dt => cascadingUpdates.push(dt));
                        }
                    }

                    // move tasks in moved project
                    if (dependentTasks.length > 0) {
                        const firstDate = [...dependentTasks].sort((a, b) => a.start - b.start)[0].start;
                        const deltaDate = taskWithDates.start - firstDate;

                        dependentTasks.forEach(element => {
                            const taskIndex = newTasks.findIndex(t => t.id === element.id);
                            const start = new Date(element.start.getTime() + deltaDate);
                            const end = new Date(element.end.getTime() + deltaDate);
                            const updatedTask = { ...element, start, end };

                            newTasks = [
                                ...newTasks.slice(0, taskIndex),
                                updatedTask,
                                ...newTasks.slice(taskIndex + 1)
                            ];
                            cascadingUpdates.push(updatedTask);
                        });
                    }

                    // shift dependent tasks (tasks that depend on this task)
                    const dependentByDependency = newTasks.filter(t =>
                        t.dependencies && t.dependencies.length > 0 && t.dependencies[0] === task.id
                    );

                    if (dependentByDependency.length > 0) {
                        const originalTask = prevTasks.find(t => t.id === task.id);
                        const startDelta = optimisticTask.start.getTime() - originalTask.start.getTime();
                        const endDelta = optimisticTask.end.getTime() - originalTask.end.getTime();

                        dependentByDependency.forEach(depTask => {
                            const taskIndex = newTasks.findIndex(t => t.id === depTask.id);
                            let start, end;
                            if (startDelta === 0 && endDelta !== 0) {
                                start = new Date(depTask.start.getTime() + endDelta);
                                end = new Date(depTask.end.getTime() + endDelta);
                            } else {
                                start = new Date(depTask.start.getTime() + startDelta);
                                end = new Date(depTask.end.getTime() + endDelta);
                            }
                            const updatedTask = { ...depTask, start, end };

                            newTasks = [
                                ...newTasks.slice(0, taskIndex),
                                updatedTask,
                                ...newTasks.slice(taskIndex + 1)
                            ];
                            cascadingUpdates.push(updatedTask);
                        });
                    }

                    // recalculate project dates/progress
                    if (taskWithDates.project || taskWithDates.type === "project") {
                        const projectId = taskWithDates.project || taskWithDates.id;
                        const [start, end] = getStartEndDateForProject(newTasks, projectId);
                        const progress = getTotalProjectProgress(newTasks, projectId, start, end);

                        const projectIndex = newTasks.findIndex(t => t.id === projectId);
                        if (projectIndex !== -1) {
                            const project = newTasks[projectIndex];
                            if (project.start.getTime() !== start.getTime() ||
                                project.end.getTime() !== end.getTime() ||
                                project.progress !== progress) {
                                const updatedProject = { ...project, start, end, progress };
                                newTasks = [
                                    ...newTasks.slice(0, projectIndex),
                                    updatedProject,
                                    ...newTasks.slice(projectIndex + 1)
                                ];
                                cascadingUpdates.push(updatedProject);
                            }
                        }
                    }

                    return newTasks;
                });

                // Dedupe by id — last entry wins (most up-to-date values)
                const updatesMap = new Map();
                updatesMap.set(taskToUpdate.id || taskToUpdate._id, taskToUpdate);
                for (const cu of cascadingUpdates) {
                    updatesMap.set(cu.id || cu._id, cu);
                }
                const allUpdates = Array.from(updatesMap.values());

                // Always send exactly ONE bulk update
                bulkUpdateTasks(allUpdates).unwrap().then(() => {
                    const taskName = task.type === 'project' ? `Projekt "${task.name}"` : `Zadanie "${task.name}"`;
                    if (allUpdates.length > 1) {
                        toast(`${taskName} i ${allUpdates.length - 1} powiązanych zadań zaktualizowane`, "info");
                    } else {
                        toast(`${taskName} zaktualizowane`, "info");
                    }
                }).catch((err) => {
                    console.error('Błąd podczas aktualizacji zadań:', err);
                    toast('Błąd podczas aktualizacji zadań', 'error');
                    try { refetchTasks(); } catch (e) { }
                }).finally(() => {
                    if (taskUpdateKey) {
                        pendingTaskUpdatesRef.current.delete(taskUpdateKey);
                    }
                });
            } catch (err) {
                if (taskUpdateKey) {
                    pendingTaskUpdatesRef.current.delete(taskUpdateKey);
                }
                console.error('Błąd podczas aktualizacji zadania:', err);
            }
        }
    };

    const expandToggleTask = (taskId) => {
        setTasks(tasks.map(t => (t.id === taskId.id ? taskId : t)));
    };

    const expandAllProjects = () => {
        setTasks(tasks.map(t => (t.type === 'project' ? { ...t, hideChildren: false } : t)));
    };

    const collapseAllProjects = () => {
        setTasks(tasks.map(t => (t.type === 'project' ? { ...t, hideChildren: true } : t)));
    };

    const expandAllDependentChains = () => {
        setCollapsedChains(new Set());
    };

    const collapseAllDependentChains = () => {
        const newCollapsed = new Set();
        tasks.forEach(task => {
            if (task.isChainParent && task.dependencyChain && task.dependencyChain.length > 1) {
                newCollapsed.add(task.id);
            }
        });
        setCollapsedChains(newCollapsed);
    };

    const handleRowHeightChange = async (newHeight) => {
        setRowHeight(newHeight);
        try {
            await updateUserSettings({ rowHeight: newHeight });
        } catch (err) {
            console.error('Failed to save rowHeight:', err);
        }
    };

    const handleColumnWidthChange = async (newWidth) => {
        setColumnWidth(newWidth);
        try {
            await updateUserSettings({ columnWidth: newWidth });
        } catch (err) {
            console.error('Failed to save columnWidth:', err);
        }
    };

    const handleBarFillChange = async (newBarFill) => {
        setBarFill(newBarFill);
        try {
            await updateUserSettings({ barFill: newBarFill });
        } catch (err) {
            console.error('Failed to save barFill:', err);
        }
    };

    const handleSortChange = async (newSort) => {
        setSortBy(newSort);
        try {
            await updateUserSettings({ sortBy: newSort });
        } catch (err) {
            console.error('Failed to save sortBy:', err);
        }
    };

    const handleColumnVisibilityChange = async (newVisibility) => {
        setColumnVisibility(newVisibility);
        try {
            await updateUserSettings({ columnVisibility: newVisibility });
        } catch (err) {
            console.error('Failed to save columnVisibility:', err);
        }
    };

    const handleColumnOrderChange = async (newOrder) => {
        setColumnOrder(newOrder);
        try {
            await updateUserSettings({ columnOrder: newOrder });
        } catch (err) {
            console.error('Failed to save columnOrder:', err);
        }
    };

    const handleColumnWidthsChange = async (newWidths, shouldSave = true) => {
        setColumnWidths(newWidths);
        
        if (shouldSave) {
            try {
                await updateUserSettings({ columnWidths: newWidths });
            } catch (err) {
                console.error('Failed to save columnWidths:', err);
            }
        }
    };

    const handleTaskReorder = async (draggedTask, targetTask) => {
        try {
            // Check user permissions
            if (user?.type !== "admin") {
                if (user?.type === "manager") {
                    const project = tasks.find(t => t.id === (draggedTask.type === 'project' ? draggedTask.id : draggedTask.project));
                    if (!project || !project.coordinators?.includes(user._id)) {
                        toast("Nie masz uprawnień do zmiany kolejności", "error");
                        return;
                    }
                } else {
                    toast("Nie masz uprawnień do zmiany kolejności", "error");
                    return;
                }
            }

            let itemsToSort = [];

            // Get items to sort based on type
            if (draggedTask.type === 'project') {
                itemsToSort = tasks
                    .filter(t => t.type === 'project')
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            } else {
                if (!draggedTask.project) return;
                itemsToSort = tasks
                    .filter(t => t.project === draggedTask.project && t.type === 'task')
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            }

            const draggedIndex = itemsToSort.findIndex(t => t.id === draggedTask.id);
            const targetIndex = itemsToSort.findIndex(t => t.id === targetTask.id);

            if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return;

            // Remove dragged item and insert at target position
            const [removed] = itemsToSort.splice(draggedIndex, 1);
            itemsToSort.splice(targetIndex, 0, removed);

            // Update displayOrder for all items in the sorted list
            const updatedItems = itemsToSort.map((item, index) => ({
                ...item,
                displayOrder: index
            }));

            // Optimistically update local state immediately for instant UI feedback
            setTasks(prevTasks => {
                return prevTasks.map(task => {
                    const updatedItem = updatedItems.find(item => item.id === task.id);
                    return updatedItem || task;
                });
            });

            // Only update items in the affected range (between old and new position) in the backend
            const minIndex = Math.min(draggedIndex, targetIndex);
            const maxIndex = Math.max(draggedIndex, targetIndex);
            
            const itemsToUpdate = updatedItems.slice(minIndex, maxIndex + 1);

            // Use bulk update to send a single request instead of multiple
            await bulkUpdateTasks(itemsToUpdate).unwrap();

            toast(`${draggedTask.type === 'project' ? 'Projekt' : 'Zadanie'} "${draggedTask.name}" przesunięte`, "success");
        } catch (err) {
            // On error, refetch to restore correct state
            refetchTasks();
            toast(`Błąd podczas zmiany kolejności: ${err.message}`, "error");
        }
    };

    useEffect(() => {
        if (isTasksLoaded && data && !isUserLoading && user) {
            const convertedData = data.map((item) => {
                // Validate and convert dates
                let startDate = item.start;
                let endDate = item.end;
                
                // Convert to Date if not already, and ensure valid date
                if (!(startDate instanceof Date)) {
                    startDate = startDate ? new Date(startDate) : new Date();
                }
                if (!(endDate instanceof Date)) {
                    endDate = endDate ? new Date(endDate) : new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
                }
                
                // Ensure dates are valid
                if (isNaN(startDate.getTime())) {
                    startDate = new Date();
                }
                if (isNaN(endDate.getTime())) {
                    endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
                }
                
                return {
                    ...item,
                    name: item.name || 'Bez nazwy',
                    start: startDate,
                    end: endDate,
                    isDisabled: user.type === "user",
                    hide: user.type === "user" && item.type === "task" && item.user !== user._id
                };
            });
            setTasks(convertedData);
        }
    }, [isTasksLoaded, data, isUserLoading, user]);

    const getNextDisplayOrder = (projectId, tasks) => {
        const projectTasks = tasks.filter(t => t.project === projectId);
        const maxOrder = projectTasks.length > 0
            ? Math.max(...projectTasks.map(t => t.displayOrder || 0))
            : 0;
        return maxOrder + 1;
    };

    const sortTasksByDisplayOrder = (tasks) => {
        const projects = tasks.filter(t => t.type === "project");
        const otherTasks = tasks.filter(t => t.type !== "project");

        if (!showProjects) {
            const projectNameById = projects.reduce((acc, project) => {
                if (project.id) acc[project.id] = project.name || 'Bez projektu';
                if (project._id) acc[project._id] = project.name || 'Bez projektu';
                return acc;
            }, {});

            const withProjectName = (task) => {
                const projectName = projectNameById[task.project] || 'Bez projektu';
                return {
                    ...task,
                    name: `${projectName} - ${task.name || ''}`
                };
            };

            const getFirstUserName = (task) => {
                const firstUserId = task.users?.[0];
                return firstUserId ? (users?.find(u => u._id === firstUserId)?.name || '') : '';
            };

            const getFirstUserBranch = (task) => {
                const firstUserId = task.users?.[0];
                return firstUserId ? (users?.find(u => u._id === firstUserId)?.branch || '') : '';
            };

            const compareText = (a, b) => {
                if (!a && !b) return 0;
                if (!a) return 1;
                if (!b) return -1;
                return a.localeCompare(b, 'pl');
            };

            const compareStartDate = (a, b) => {
                const aDate = a?.start ? new Date(a.start).getTime() : Number.MAX_SAFE_INTEGER;
                const bDate = b?.start ? new Date(b.start).getTime() : Number.MAX_SAFE_INTEGER;
                return aDate - bDate;
            };

            switch (sortBy) {
                case 'coordinator':
                case 'users':
                    return [...otherTasks].sort((a, b) => compareText(getFirstUserName(a), getFirstUserName(b))).map(withProjectName);

                case 'branch':
                    return [...otherTasks].sort((a, b) => compareText(getFirstUserBranch(a), getFirstUserBranch(b))).map(withProjectName);

                case 'projectName':
                    return [...otherTasks].sort((a, b) => compareText(a.name || '', b.name || '')).map(withProjectName);

                case 'startDate':
                    return [...otherTasks].sort(compareStartDate).map(withProjectName);

                default:
                    return [...otherTasks].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map(withProjectName);
            }
        }

        const tasksByProject = otherTasks.reduce((acc, task) => {
            const projectId = task.project;
            if (!acc[projectId]) {
                acc[projectId] = [];
            }
            acc[projectId].push(task);
            return acc;
        }, {});

        let sortedProjects;
        
        switch (sortBy) {
            case 'coordinator':
                sortedProjects = projects.sort((a, b) => {
                    const aCoord = a.coordinators && a.coordinators.length > 0 
                        ? users?.find(u => u._id === a.coordinators[0])?.name || ''
                        : '';
                    const bCoord = b.coordinators && b.coordinators.length > 0
                        ? users?.find(u => u._id === b.coordinators[0])?.name || ''
                        : '';
                    // Puste wartości na końcu
                    if (!aCoord && !bCoord) return 0;
                    if (!aCoord) return 1;
                    if (!bCoord) return -1;
                    return aCoord.localeCompare(bCoord);
                });
                break;
            
            case 'projectName':
                sortedProjects = projects.sort((a, b) => {
                    const aName = a.name || '';
                    const bName = b.name || '';
                    // Puste wartości na końcu
                    if (!aName && !bName) return 0;
                    if (!aName) return 1;
                    if (!bName) return -1;
                    return aName.localeCompare(bName);
                });
                break;

            case 'startDate':
                sortedProjects = projects.sort((a, b) => {
                    const aDate = a?.start ? new Date(a.start).getTime() : Number.MAX_SAFE_INTEGER;
                    const bDate = b?.start ? new Date(b.start).getTime() : Number.MAX_SAFE_INTEGER;
                    return aDate - bDate;
                });
                break;
            
            case 'users':
                sortedProjects = projects.sort((a, b) => {
                    const aUserIds = tasksByProject[a.id] 
                        ? [...new Set(tasksByProject[a.id].flatMap(t => t.users || []))]
                        : [];
                    const bUserIds = tasksByProject[b.id]
                        ? [...new Set(tasksByProject[b.id].flatMap(t => t.users || []))]
                        : [];
                    const aUserName = aUserIds.length > 0
                        ? users?.find(u => u._id === aUserIds[0])?.name || ''
                        : '';
                    const bUserName = bUserIds.length > 0
                        ? users?.find(u => u._id === bUserIds[0])?.name || ''
                        : '';
                    // Puste wartości na końcu
                    if (!aUserName && !bUserName) return 0;
                    if (!aUserName) return 1;
                    if (!bUserName) return -1;
                    return aUserName.localeCompare(bUserName);
                });
                break;
            
            case 'branch':
                sortedProjects = projects.sort((a, b) => {
                    const aUserIds = tasksByProject[a.id]
                        ? [...new Set(tasksByProject[a.id].flatMap(t => t.users || []))]
                        : [];
                    const bUserIds = tasksByProject[b.id]
                        ? [...new Set(tasksByProject[b.id].flatMap(t => t.users || []))]
                        : [];
                    const aBranch = aUserIds.length > 0
                        ? users?.find(u => u._id === aUserIds[0])?.branch || ''
                        : '';
                    const bBranch = bUserIds.length > 0
                        ? users?.find(u => u._id === bUserIds[0])?.branch || ''
                        : '';
                    // Puste wartości na końcu
                    if (!aBranch && !bBranch) return 0;
                    if (!aBranch) return 1;
                    if (!bBranch) return -1;
                    return aBranch.localeCompare(bBranch);
                });
                break;
            
            default: // 'default'
                sortedProjects = projects.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                break;
        }

        const sorted = sortedProjects
            .flatMap(project => {
                const sortedTasksInProject = (tasksByProject[project.id] || [])
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                return [project, ...sortedTasksInProject];
            });

        return sorted;
    };

    return (
        <>
            <TopMenu
                onViewModeChange={setView}
                view={view}
                onChangeProject={handleProjectChange}
                onChangeSelectedUser={(e) => setSelectedUser([...e.value])}
                onChangeSelectedBranch={setSelectedBranch}
                project={projectFilter}
                users={users}
                institutions={institutions}
                selectedUser={selectedUser}
                selectedBranch={selectedBranch}
                selectedProjectStatus={selectedProjectStatus}
                onChangeProjectStatus={setSelectedProjectStatus}
                showProjects={showProjects}
                onShowProjectsChange={setShowProjects}
                tasks={tasks}
                onChangeEditorMode={setEditorMode}
                onExpandAll={expandAllProjects}
                onCollapseAll={collapseAllProjects}
                onExpandAllChains={expandAllDependentChains}
                onCollapseAllChains={collapseAllDependentChains}
                rowHeight={rowHeight}
                onRowHeightChange={handleRowHeightChange}
                columnWidth={columnWidth}
                onColumnWidthChange={handleColumnWidthChange}
                barFill={barFill}
                onBarFillChange={handleBarFillChange}
                sortBy={sortBy}
                onSortChange={handleSortChange}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onCreateProjectRequest={(draftProject) => {
                    setProjectModalProject(draftProject);
                    setProjectModalVisible(true);
                }}
            />

            <div
                ref={ganttContainerRef}
                className="gantt-view-shell"
            >
                {(isInitialGanttLoading || isGanttRefreshing) && (
                    <div className="gantt-loading-overlay" aria-live="polite" aria-busy="true">
                        <div className="gantt-loading-card">
                            <div className="gantt-loading-ring" />
                            <div className="gantt-loading-text">
                                {isInitialGanttLoading ? 'Ładowanie widoku...' : 'Odświeżanie widoku...'}
                            </div>
                        </div>
                    </div>
                )}
                {!isInitialGanttLoading && (
                    <>
                        <Dialog visible={visible} style={{ backgroundColor: "#ffeeee", color: "#ff5858", borderLeft: "5px solid #ff5858" }} onHide={() => { if (!visible) return; setVisible(false); }}
                            content={({ hide }) => (
                                <div style={{ padding: "2rem" }}>
                                    <i className="pi pi-times" style={{ fontSize: '2rem', width: "100%", textAlign: "right" }} onClick={(e) => hide(e)}></i>
                                    <div style={{ display: "flex", alignItems: "center" }} >

                                        <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', marginRight: '1rem' }}></i>
                                        <p>  zadanie zależne nie może rozpoczynać się <br /> wcześniej niż zadanie nadrzędne</p>
                                    </div></div>)}>

                        </Dialog>
                        <GanttChart
                    key={`gantt-${projectFilter._id}-${selectedUser.join(',')}-${selectedBranch.join(',')}-${selectedProjectStatus}`}
                    tasks={sortTasksByDisplayOrder(
                        tasks.filter(task => {
                            // Always hide tasks marked as hidden
                            if (task.hide) return false;

                            // Helper to check if task matches branch filter
                            const matchesBranchFilter = (task) => {
                                if (!selectedBranch || selectedBranch.length === 0) return true;
                                return task.users?.some(userId => {
                                    const userBranch = users?.find(u => u._id === userId)?.branch;
                                    return selectedBranch.includes(userBranch);
                                });
                            };

                            // For projects
                            if (task.type === "project") {
                                // If no project filter or this is the filtered project
                                if (task._id === projectFilter._id || projectFilter._id === 'all') {
                                    // If no filters applied, show the project
                                    if (selectedUser?.length === 0 && selectedBranch?.length === 0) {
                                        return true;
                                    }
                                    
                                    // Check if project has any tasks that match user and branch filters
                                    const projectTasks = tasks.filter(t => 
                                        t.type === "task" && 
                                        t.project === task._id &&
                                        (selectedUser?.length === 0 || t.users?.some(userId => selectedUser.includes(userId))) &&
                                        matchesBranchFilter(t)
                                    );
                                    
                                    // Show project only if it has matching tasks
                                    return projectTasks.length > 0;
                                }
                                return false;
                            }

                            // For tasks - filter by user and branch
                            // (status filtering is handled by backend via useGetTasksQuery)
                            const userMatch = selectedUser?.length === 0 || task.users?.some(userId => selectedUser.includes(userId));
                            const branchMatch = matchesBranchFilter(task);
                            const projectMatch = projectFilter._id === 'all' || task?.project === projectFilter._id;
                            
                            return userMatch && branchMatch && projectMatch;
                        })
                    )}
                    view={view}
                    viewDate={viewDate}
                    isChecked={true}
                    onTaskChange={user?.type === "admin" || (user?.type === "manager" && editorMode) ? handleTaskChange : undefined}
                    onProgressChange={user?.type !== "user" ? handleTaskChange : undefined}
                    onRightClick={handleDoubleClick}
                    onExpanderClick={expandToggleTask}
                    onTaskReorder={handleTaskReorder}
                    rowHeight={rowHeight}
                    columnWidth={columnWidth}
                    barFill={barFill}
                    columnVisibility={columnVisibility}
                    columnOrder={columnOrder}
                    onColumnOrderChange={handleColumnOrderChange}
                    columnWidths={columnWidths}
                    onColumnWidthsChange={handleColumnWidthsChange}
                    collapsedChains={collapsedChains}
                    onChainToggle={(chainId) => {
                        const newChains = new Set(collapsedChains);
                        if (newChains.has(chainId)) {
                            newChains.delete(chainId);
                        } else {
                            newChains.add(chainId);
                        }
                        setCollapsedChains(newChains);
                    }}
                />
                    </>
                )}
            </div>


            <TaskModal
                task={modalTask}
                visible={modalVisible}
                onHide={() => setModalVisible(false)}
                usersData={users}
                institutionsData={institutions}
                onSubmit={async (task) => {
                    if (task._id) {
                        // Existing task - update
                        await handleTaskChange(task);
                    } else {
                        // New task - create
                        try {
                            const taskToCreate = {
                                ...task,
                                start: task.start instanceof Date ? task.start.toISOString() : task.start,
                                end: task.end instanceof Date ? task.end.toISOString() : task.end
                            };
                            await createTask(taskToCreate).unwrap();
                            refetchTasks();
                            toast(`Zadanie "${task.name}" utworzone`, "success");
                        } catch (err) {
                            console.error('Failed to create task:', err);
                            toast(`Błąd podczas tworzenia zadania`, "error");
                        }
                    }
                    setModalVisible(false);
                }}
                projects={tasks.filter(t => t.type === 'project')}
                tasks={tasks}
                onDelete={async (task) => {
                    try {
                        await deleteTask(task).unwrap();
                        refetchTasks();
                    } catch (err) {
                        console.error('Failed to delete task:', err);
                    }
                }}
                onAddDependentTask={async (type) => {
                    try {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const endDate = new Date(today.getTime());
                        endDate.setMonth(endDate.getMonth() + 1);
                        const newTask = {
                            name: '',
                            type: type,
                            progress: 0,
                            start: today.toISOString(),
                            end: endDate.toISOString(),
                            dependencies: [modalTask.id],
                            project: type === "task" ? modalTask.project : modalTask.id,
                            hideChildren: false,
                            users: type === "task" ? (selectedUser || []) : undefined,
                            displayOrder: getNextDisplayOrder(
                                type === "task" ? modalTask.project : modalTask.id,
                                tasks
                            )
                        };
                        await createTask(newTask).unwrap();
                        refetchTasks();
                    } catch (err) {
                        console.error('Failed to create task:', err);
                    }
                }}
            />

            <ProjectModal
                allTasks={tasks}
                usersData={users}
                institutionsData={institutions}
                visibleTasks={sortTasksByDisplayOrder(
                    tasks.filter(task => {
                        // Always hide tasks marked as hidden
                        if (task.hide) return false;

                        // Helper to check if task matches branch filter
                        const matchesBranchFilter = (task) => {
                            if (!selectedBranch || selectedBranch.length === 0) return true;
                            return task.users?.some(userId => {
                                const userBranch = users?.find(u => u._id === userId)?.branch;
                                return selectedBranch.includes(userBranch);
                            });
                        };

                        // For projects
                        if (task.type === "project") {
                            // If no project filter or this is the filtered project
                            if (task._id === projectFilter._id || projectFilter._id === 'all') {
                                // If no filters applied, show the project
                                if (selectedUser?.length === 0 && selectedBranch?.length === 0) {
                                    return true;
                                }
                                
                                // Check if project has any tasks that match user and branch filters
                                const projectTasks = tasks.filter(t => 
                                    t.type === "task" && 
                                    t.project === task._id &&
                                    (selectedUser?.length === 0 || t.users?.some(userId => selectedUser.includes(userId))) &&
                                    matchesBranchFilter(t)
                                );
                                
                                // Show project only if it has matching tasks
                                return projectTasks.length > 0;
                            }
                            return false;
                        }

                        // For tasks - filter by user and branch
                        // (status filtering is handled by backend via useGetTasksQuery)
                        const userMatch = selectedUser?.length === 0 || task.users?.some(userId => selectedUser.includes(userId));
                        const branchMatch = matchesBranchFilter(task);
                        const projectMatch = projectFilter._id === 'all' || task?.project === projectFilter._id;
                        
                        return userMatch && branchMatch && projectMatch;
                    })
                )}
                project={projectModalProject}
                visible={projectModalVisible}
                onHide={() => {
                    setProjectModalVisible(false);
                    setProjectModalProject(null);
                }}
                acceptLabel="Tak"
                rejectLabel="Nie"
                onSubmit={async (project) => {
                    if (project._id) {
                        // Existing project - update
                        await handleTaskChange(project);
                    } else {
                        // New project - create
                        try {
                            const projectToCreate = {
                                ...project,
                                start: project.start instanceof Date ? project.start.toISOString() : project.start,
                                end: project.end instanceof Date ? project.end.toISOString() : project.end
                            };
                            await createTask(projectToCreate).unwrap();
                            refetchTasks();
                            toast(`Projekt "${project.name}" utworzony`, "success");
                        } catch (err) {
                            console.error('Failed to create project:', err);
                            toast(`Błąd podczas tworzenia projektu`, "error");
                        }
                    }
                    setProjectModalVisible(false);
                    setProjectModalProject(null);
                }}
                onDelete={async (projectToDelete) => {
                    try {
                        await deleteTask(projectToDelete).unwrap();
                        refetchTasks();
                    } catch (err) {
                        console.error('Failed to delete project:', err);
                    }
                }}
            />

            <ConfirmDialog
                visible={deleteConfirmVisible}
                onHide={() => setDeleteConfirmVisible(false)}
                message={taskToDelete ? `Czy na pewno chcesz usunąć "${taskToDelete.name}"?` : ''}
                header="Potwierdzenie"
                icon="pi pi-exclamation-triangle"
                acceptLabel="Tak"
                rejectLabel="Nie"
                accept={() => {
                    if (taskToDelete) {
                        deleteTask(taskToDelete)
                            .unwrap()
                            .then(() => {
                                refetchTasks();
                                toast(taskToDelete.type === 'project' ? `Projekt "${taskToDelete.name}" usunięty` : `Zadanie "${taskToDelete.name}" usunięte`, "error");
                            })
                            .catch(err => {
                                toast(`Błąd podczasu usuwania zadania: ${err.message}`, "error");
                            });

                    }
                    setDeleteConfirmVisible(false);


                }}
                reject={() => setDeleteConfirmVisible(false)}
            />

            {contextMenu.visible && contextMenu.task && (
                <TaskContextMenu
                    tasks={tasks}
                    task={contextMenu.task}
                    visible={contextMenu.visible}
                    position={contextMenu.position}
                    acceptLabel="Tak"
                    rejectLabel="Nie"
                    selectedUser={selectedUser}
                    onHide={() => setContextMenu({ ...contextMenu, visible: false })}
                    onTaskSelected={(taskId) => {
                        const task = tasks.find(t => t._id === taskId);
                        if (task) {
                            if (task.type === 'project') {
                                setProjectModalProject(task);
                                setProjectModalVisible(true);
                            } else {
                                setModalTask(task);
                                setModalVisible(true);
                            }
                        }
                    }}
                    onCreateTaskRequest={(draftTask) => {
                        setModalTask(draftTask);
                        setModalVisible(true);
                    }}
                    onDeleteRequest={(task) => {
                        setTaskToDelete(task);
                        setDeleteConfirmVisible(true);
                        setContextMenu({ ...contextMenu, visible: false });
                    }}
                    refetchTasks={refetchTasks}
                    projects={tasks.filter(t => t.type === 'project')}
                />
            )}
        </>
    );
};

export default TaskManagementPage;





